import React, { useEffect, useState } from 'react';
import { useVimUserMetadata } from '@getvim/internal-vim-os-sdk/react';
import { createLogger } from '../utils';
import { AppConfig } from '../types';
import { internalApi } from '../api';
import { AppFeatureFlags, AppFeatureFlagsProvider } from '../utils/appFeatureFlagsContext';
import { getAppFeatureFlags } from '../utils/featureFlags';
import App from './App';

const logger = createLogger('referral-utility:AppWrapper');

const AppWrapper = () => {
  const [appConfig, setAppConfig] = useState<AppConfig>();
  const [appFeatureFlags, setAppFeatureFlags] = useState<AppFeatureFlags>();
  const { user, organization } = useVimUserMetadata();

  useEffect(() => {
    // Resolve appConfig and every feature flag useEhrStateHandlers depends on together, before
    // <App> (and useEhrStateHandlers) ever mounts — the hook can then only ever read final flag
    // values, no loading/default-value race to guard against.
    //
    // Deliberately no timeout/race here: if the flag service is degraded, the underlying client
    // already retries for up to ~2 minutes (see getFlagOrDefault's error log for that case) before
    // falling back to defaults. Until then this renders nothing, same as a disabled app — there's
    // no user-facing difference between "not yet activated" and "can't be opened anyway", so a
    // shorter fallback here wouldn't change what the user can actually do.
    //
    // [] deps is safe here only because user/organization come from VimSDKProvider's
    // sessionContext, which is already fully resolved before this component can mount (that
    // provider itself renders null until its async init completes) — not from state that could
    // still be loading inside this tree. If RUFlagContext ever grows a field sourced from state
    // that changes after mount, this effect would need that field in its deps to stay correct.
    Promise.all([
      internalApi.referralUtilityApi.getConfiguration(),
      getAppFeatureFlags({
        vimUserEmail: user?.identifiers?.ehrUsername,
        organizationId_string: organization?.identifiers?.id.toString(),
      }),
    ])
      .then(([data, flags]) => {
        const { configuration } = data;
        logger.info('Got app config', {
          optumDeployConfiguration: configuration?.optumDeployConfiguration,
          ...flags,
          noPHI: true,
        });
        setAppConfig(configuration!);
        setAppFeatureFlags(flags);
      })
      .catch((error) => {
        logger.error(error);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return appConfig && appFeatureFlags ? (
    <AppFeatureFlagsProvider value={appFeatureFlags}>
      <App appConfig={appConfig} />
    </AppFeatureFlagsProvider>
  ) : null;
};

export default AppWrapper;
